"use client";

import { renderPercentage } from "@/apiUtils/commonUtils";
import RenderPrice from "@/components/productPrice/RenderPrice";
import { cn } from "@/lib/tailwind";

interface IProps {
  hasCost: boolean;
  lang: string;
  quantity: number;
  currency: string;
  preview?: boolean;
  discount: number | null;
  listPrice: number;
  isFlatOff: boolean;
  salePrice?: number;
  isOutOfStock: boolean;
  isDiscounted?: boolean;
  // TODO(QA): When Qatar (qa) is enabled, use this to gate any % badge (same as Magrabi CartItemPricing).
  displayPercentage?: boolean;
  freeLabel?: string;
  qtyLabel?: string;
  showQuantityControls?: boolean;
  onChangeQuantity: (quantity: number) => void;
}

const CartPricing = ({
  hasCost,
  isOutOfStock,
  quantity,
  currency,
  lang,
  isFlatOff,
  listPrice,
  salePrice,
  isDiscounted,
  discount,
  displayPercentage,
  preview = false,
  freeLabel,
  qtyLabel,
  showQuantityControls = false,
  onChangeQuantity,
}: IProps) => {
  const quantitySelector =
    !preview && (hasCost || showQuantityControls) ? (
      <div className="mt-3 flex items-center justify-end gap-3">
        <button
          type="button"
          disabled={isOutOfStock || quantity == 1}
          onClick={() => onChangeQuantity(quantity === 1 ? quantity : quantity - 1)}
          className={cn(
            "flex size-4 items-center justify-center rounded bg-[#3A3A3A] text-white disabled:bg-tertiary-9",
          )}
        >
          -
        </button>
        <span className="text-sm">{quantity}</span>
        <button
          type="button"
          disabled={isOutOfStock}
          onClick={() => onChangeQuantity(quantity + 1)}
          className="flex size-4 items-center justify-center rounded bg-[#3A3A3A] text-white"
        >
          +
        </button>
      </div>
    ) : null;

  if (!hasCost) {
    return (
      <div className="min-w-max">
        <p className={cn("text-sm font-semibold text-success")}>{freeLabel ?? "Free"}</p>
        {quantity > 1 ? (
          <p className="mt-1 text-end text-sm text-black">
            {qtyLabel ?? "Qty"}: {quantity}
          </p>
        ) : null}
        {quantitySelector}
      </div>
    );
  }

  const discountLabel =
    (isDiscounted || isFlatOff) && discount != null && (isFlatOff || displayPercentage) ? (
      <p className="text-sm font-semibold text-tertiary-6">
        {isFlatOff ? (
          <RenderPrice price={discount} currencyCode={currency} lang={lang} isNegative />
        ) : (
          renderPercentage(lang, discount, true)
        )}
      </p>
    ) : null;

  return (
    <div className="min-w-max">
      <div className="hidden md:flex md:gap-2">
        {(isDiscounted || isFlatOff) && (
          <p className="text-sm font-normal text-tertiary-6">
            <RenderPrice
              price={listPrice ?? 0}
              currencyCode={currency}
              lang={lang}
              isStrikethrough
            />
          </p>
        )}
        <p
          className={cn(
            "text-sm font-semibold",
            (isDiscounted || isFlatOff) && "text-secondary-1",
          )}
        >
          <RenderPrice price={salePrice ?? 0} currencyCode={currency} lang={lang} />
        </p>
        {discountLabel}
      </div>
      <div className="flex flex-col items-end md:hidden">
        <div className="flex gap-2">
          {(isDiscounted || isFlatOff) && (
            <p className="text-sm font-normal text-tertiary-6">
              <RenderPrice
                price={listPrice ?? 0}
                currencyCode={currency}
                lang={lang}
                isStrikethrough
              />
            </p>
          )}
          <p
            className={cn(
              "text-sm font-semibold",
              (isDiscounted || isFlatOff) && "text-secondary-1",
            )}
          >
            <RenderPrice price={salePrice ?? 0} currencyCode={currency} lang={lang} />
          </p>
          {discountLabel}
        </div>
      </div>

      {quantitySelector}
    </div>
  );
};

export default CartPricing;
